import axios from "axios";
import { Country, Product, PaymentCountry, PaymentState, CreditCardType, CreditCardSettings, ProductTerms } from "../types";
import { authHook, fetchGuestEmail } from './helper-service';
import { CreditCardFactory } from '../common/credit-card-factory';
import { isDev } from "src/utils";
export class CatalogService {
    static async getSettings() {
        const response = await axios.get(`/api/catalog/settings`);
        return response.data.payLoad;
    }
    static async getCountries() {
        const response = await axios.get(`/api/catalog/countries`);
        return response.data.payLoad as Country[];
    }
    static async getProducts(countryCode: string, sourceCurrencyCode: string) {
        const response = await axios.get(`/api/catalog/products/${countryCode}/${sourceCurrencyCode}`);
        return response.data.payLoad as { products: Product[] };
    }
    static async getGiftCardProducts(countryCode: string, sourceCurrencyCode: string) {
        const response = await axios.get(`/api/catalog/products-giftcard/${countryCode}/${sourceCurrencyCode}`);
        return response.data.payLoad as { products: Product[] };
    }
    static async getProductTermsAndConditions(productId: number) {
        const response = await axios.get(`/api/catalog/products-terms-conditions/${productId}`);
        return response.data.payLoad as ProductTerms;
    }
    static async detectOperator(countryCode: string, internationalCode: number, mobileNumber: string) {
        // await TopupService.delay(5000);
        // return {responseCode: 0, payLoad: {productId: 33}};
        try {
            const response = await axios.get(`/api/catalog/product/${countryCode}/${internationalCode}/${mobileNumber}`, { timeout: 10000 });
            const data = response.data as { responseCode: number, payLoad?: { productId: number } };
            return data;
        }
        catch {
            return { responseCode: -100 };
        }
    }
    static async FetchPaymentCountries() {
        const response = await axios.get(`/api/catalog/credit-card-countries`);
        return response.data.payLoad as PaymentCountry[];
    }
    static async FetchPaymentStates() {
        const response = await axios.get(`/api/catalog/credit-card-states`);
        return response.data.payLoad as PaymentState[];
    }
    static async getRewardPolicyText() {
        const response = await axios.get(`/api/catalog/reward-policy`);
        return response.data.payLoad;
    }
    @authHook
    static async fetchCardsAndCounties(paymentSessionId: string, sourceCurrencyCode: string) {
        const guestEmail = fetchGuestEmail();
        const response = await axios.get(`/api/trx/stored-cards-countries?paymentSessionId=${paymentSessionId}&sourceCurrencyCode=${sourceCurrencyCode}&isGuest=${guestEmail ? "true" : "false"}`,
            {
                withCredentials: true
            });
        //return response.data.payLoad as {cards: StoredCreditCard[], countries: PaymentCountry[]};
        for (let i = 0; i < response.data.payLoad.cards.length; i++) {
            const c = response.data.payLoad.cards[i];
            c.cardType = c.cardType.toLowerCase() as CreditCardType;
            const [mm, yyyy]: [string, string] = c.expiryMonthyear.split("/");
            const yy = yyyy.length === 4 ? yyyy.substr(2) : yyyy;
            c.expiryMonthyear = `${mm}/${yy}`;
        }
        const countryCache = this.getPaymentCountries();
        const countries = response.data.payLoad.countries.map((c: any) => {
            if (!c.states) c.states = [];
            const cache = countryCache.find(cc => cc.countryCode === c.countryCode);
            if (!cache) return c;
            if (c.states.length === 0) return cache;
            return c;
        });
        response.data.payLoad.countries = countries;
        return response.data as {
            responseCode: number, responseMessage: string, payLoad: CreditCardSettings
        };
    }
    static getPaymentCountries() {
        const us = { countryCode: 'US', countryName: 'United States of America', allowsZipCode: true, states: [] as PaymentState[] };
        const uk = { countryCode: 'GB', countryName: 'United Kingdom', allowsZipCode: true, states: [] as PaymentState[] };
        const ca = { countryCode: 'CA', countryName: 'Canada', allowsZipCode: true, states: [] as PaymentState[] };
        us.states = [
            { stateCode: 'AL', stateName: 'Alabama' },
            { stateCode: 'AK', stateName: 'Alaska' },
            { stateCode: 'AZ', stateName: 'Arizona' },
            { stateCode: 'AR', stateName: 'Arkansas' },
            { stateCode: 'CA', stateName: 'California' },
            { stateCode: 'CO', stateName: 'Colorado' },
            { stateCode: 'CT', stateName: 'Connecticut' },
            { stateCode: 'DE', stateName: 'Delaware' },
            { stateCode: 'FL', stateName: 'Florida' },
            { stateCode: 'GA', stateName: 'Georgia' },
            { stateCode: 'HI', stateName: 'Hawaii' },
            { stateCode: 'ID', stateName: 'Idaho' },
            { stateCode: 'IL', stateName: 'Illinois' },
            { stateCode: 'IN', stateName: 'Indiana' },
            { stateCode: 'IA', stateName: 'Iowa' },
            { stateCode: 'KS', stateName: 'Kansas' },
            { stateCode: 'KY', stateName: 'Kentucky' },
            { stateCode: 'LA', stateName: 'Louisiana' },
            { stateCode: 'ME', stateName: 'Maine' },
            { stateCode: 'MD', stateName: 'Maryland' },
            { stateCode: 'MA', stateName: 'Massachusetts' },
            { stateCode: 'MI', stateName: 'Michigan' },
            { stateCode: 'MN', stateName: 'Minnesota' },
            { stateCode: 'MS', stateName: 'Mississippi' },
            { stateCode: 'MO', stateName: 'Missouri' },
            { stateCode: 'MT', stateName: 'Montana' },
            { stateCode: 'NE', stateName: 'Nebraska' },
            { stateCode: 'NV', stateName: 'Nevada' },
            { stateCode: 'NH', stateName: 'New Hampshire' },
            { stateCode: 'NJ', stateName: 'New Jersey' },
            { stateCode: 'NM', stateName: 'New Mexico' },
            { stateCode: 'NY', stateName: 'New York' },
            { stateCode: 'NC', stateName: 'North Carolina' },
            { stateCode: 'ND', stateName: 'North Dakota' },
            { stateCode: 'OH', stateName: 'Ohio' },
            { stateCode: 'OK', stateName: 'Oklahoma' },
            { stateCode: 'OR', stateName: 'Oregon' },
            { stateCode: 'PA', stateName: 'Pennsylvania' },
            { stateCode: 'RI', stateName: 'Rhode Island' },
            { stateCode: 'SC', stateName: 'South Carolina' },
            { stateCode: 'SD', stateName: 'South Dakota' },
            { stateCode: 'TN', stateName: 'Tennessee' },
            { stateCode: 'TX', stateName: 'Texas' },
            { stateCode: 'UT', stateName: 'Utah' },
            { stateCode: 'VT', stateName: 'Vermont' },
            { stateCode: 'VA', stateName: 'Virginia' },
            { stateCode: 'WA', stateName: 'Washington' },
            { stateCode: 'WV', stateName: 'West Virginia' },
            { stateCode: 'WI', stateName: 'Wisconsin' },
            { stateCode: 'WY', stateName: 'Wyoming' },
        ];
        uk.states = [
            { stateCode: 'I0', stateName: 'Aberconwy and Colwyn' },
            { stateCode: 'I1', stateName: 'Aberdeen City' },
            { stateCode: 'I2', stateName: 'Aberdeenshire' },
            { stateCode: 'I3', stateName: 'Anglesey' },
            { stateCode: 'I4', stateName: 'Angus' },
            { stateCode: 'I5', stateName: 'Antrim' },
            { stateCode: 'I6', stateName: 'Argyll and Bute' },
            { stateCode: 'I7', stateName: 'Armagh' },
            { stateCode: 'I8', stateName: 'Avon' },
            { stateCode: 'I9', stateName: 'Ayrshire' },
            { stateCode: 'IB', stateName: 'Bath and NE Somerset' },
            { stateCode: 'IC', stateName: 'Bedfordshire' },
            { stateCode: 'IE', stateName: 'Belfast' },
            { stateCode: 'IF', stateName: 'Berkshire' },
            { stateCode: 'IG', stateName: 'Berwickshire' },
            { stateCode: 'IH', stateName: 'BFPO' },
            { stateCode: 'II', stateName: 'Blaenau Gwent' },
            { stateCode: 'IJ', stateName: 'Buckinghamshire' },
            { stateCode: 'IK', stateName: 'Caernarfonshire' },
            { stateCode: 'IM', stateName: 'Caerphilly' },
            { stateCode: 'IO', stateName: 'Caithness' },
            { stateCode: 'IP', stateName: 'Cambridgeshire' },
            { stateCode: 'IQ', stateName: 'Cardiff' },
            { stateCode: 'IR', stateName: 'Cardiganshire' },
            { stateCode: 'IS', stateName: 'Carmarthenshire' },
            { stateCode: 'IT', stateName: 'Ceredigion' },
            { stateCode: 'IU', stateName: 'Channel Islands' },
            { stateCode: 'IV', stateName: 'Cheshire' },
            { stateCode: 'IW', stateName: 'City of Bristol' },
            { stateCode: 'IX', stateName: 'Clackmannanshire' },
            { stateCode: 'IY', stateName: 'Clwyd' },
            { stateCode: 'IZ', stateName: 'Conwy' },
            { stateCode: 'J0', stateName: 'Cornwall/Scilly' },
            { stateCode: 'J1', stateName: 'Cumbria' },
            { stateCode: 'J2', stateName: 'Denbighshire' },
            { stateCode: 'J3', stateName: 'Derbyshire' },
            { stateCode: 'J4', stateName: 'Derry/Londonderry' },
            { stateCode: 'J5', stateName: 'Devon' },
            { stateCode: 'J6', stateName: 'Dorset' },
            { stateCode: 'J7', stateName: 'Down' },
            { stateCode: 'J8', stateName: 'Dumfries and Galloway' },
            { stateCode: 'J9', stateName: 'Dunbartonshire' },
            { stateCode: 'JA', stateName: 'Dundee' },
            { stateCode: 'JB', stateName: 'Durham' },
            { stateCode: 'JC', stateName: 'Dyfed' },
            { stateCode: 'JD', stateName: 'East Ayrshire' },
            { stateCode: 'JE', stateName: 'East Dunbartonshire' },
            { stateCode: 'JF', stateName: 'East Lothian' },
            { stateCode: 'JG', stateName: 'East Renfrewshire' },
            { stateCode: 'JH', stateName: 'East Riding Yorkshire' },
            { stateCode: 'JI', stateName: 'East Sussex' },
            { stateCode: 'JJ', stateName: 'Edinburgh' },
            { stateCode: 'JK', stateName: 'England' },
            { stateCode: 'JL', stateName: 'Essex' },
            { stateCode: 'JM', stateName: 'Falkirk' },
            { stateCode: 'JN', stateName: 'Fermanagh' },
            { stateCode: 'JO', stateName: 'Fife' },
            { stateCode: 'JP', stateName: 'Flintshire' },
            { stateCode: 'JQ', stateName: 'Glasgow' },
            { stateCode: 'JR', stateName: 'Gloucestershire' },
            { stateCode: 'JS', stateName: 'Greater London' },
            { stateCode: 'JT', stateName: 'Greater Manchester' },
            { stateCode: 'JU', stateName: 'Gwent' },
            { stateCode: 'JV', stateName: 'Gwynedd' },
            { stateCode: 'JW', stateName: 'Hampshire' },
            { stateCode: 'JX', stateName: 'Hartlepool' },
            { stateCode: 'HAW', stateName: 'Hereford and Worcester' },
            { stateCode: 'JY', stateName: 'Hertfordshire' },
            { stateCode: 'JZ', stateName: 'Highlands' },
            { stateCode: 'K0', stateName: 'Inverclyde' },
            { stateCode: 'K1', stateName: 'Inverness-Shire' },
            { stateCode: 'K2', stateName: 'Isle of Man' },
            { stateCode: 'K3', stateName: 'Isle of Wight' },
            { stateCode: 'K4', stateName: 'Kent' },
            { stateCode: 'K5', stateName: 'Kincardinshire' },
            { stateCode: 'K6', stateName: 'Kingston Upon Hull' },
            { stateCode: 'K7', stateName: 'Kinross-Shire' },
            { stateCode: 'K8', stateName: 'Kirklees' },
            { stateCode: 'K9', stateName: 'Lanarkshire' },
            { stateCode: 'KA', stateName: 'Lancashire' },
            { stateCode: 'KB', stateName: 'Leicestershire' },
            { stateCode: 'KC', stateName: 'Lincolnshire' },
            { stateCode: 'KD', stateName: 'Londonderry' },
            { stateCode: 'KE', stateName: 'Merseyside' },
            { stateCode: 'KF', stateName: 'Merthyr Tydfil' },
            { stateCode: 'KG', stateName: 'Mid Glamorgan' },
            { stateCode: 'KI', stateName: 'Mid Lothian' },
            { stateCode: 'KH', stateName: 'Middlesex' },
            { stateCode: 'KJ', stateName: 'Monmouthshire' },
            { stateCode: 'KK', stateName: 'Moray' },
            { stateCode: 'KL', stateName: 'Neath & Port Talbot' },
            { stateCode: 'KM', stateName: 'Newport' },
            { stateCode: 'KN', stateName: 'Norfolk' },
            { stateCode: 'KP', stateName: 'North Ayrshire' },
            { stateCode: 'KQ', stateName: 'North East Lincolnshire' },
            { stateCode: 'KR', stateName: 'North Lanarkshire' },
            { stateCode: 'KT', stateName: 'North Lincolnshire' },
            { stateCode: 'KU', stateName: 'North Somerset' },
            { stateCode: 'KV', stateName: 'North Yorkshire' },
            { stateCode: 'KO', stateName: 'Northamptonshire' },
            { stateCode: 'KW', stateName: 'Northern Ireland' },
            { stateCode: 'KX', stateName: 'Northumberland' },
            { stateCode: 'KZ', stateName: 'Nottinghamshire' },
            { stateCode: 'L0', stateName: 'Orkney and Shetland Isles' },
            { stateCode: 'L1', stateName: 'Oxfordshire' },
            { stateCode: 'L2', stateName: 'Pembrokeshire' },
            { stateCode: 'L3', stateName: 'Perth and Kinross' },
            { stateCode: 'L4', stateName: 'Powys' },
            { stateCode: 'L5', stateName: 'Redcar and Cleveland' },
            { stateCode: 'L6', stateName: 'Renfrewshire' },
            { stateCode: 'L7', stateName: 'Rhonda Cynon Taff' },
            { stateCode: 'L8', stateName: 'Rutland' },
            { stateCode: 'L9', stateName: 'Scottish Borders' },
            { stateCode: 'LB', stateName: 'Shetland' },
            { stateCode: 'LC', stateName: 'Shropshire' },
            { stateCode: 'LD', stateName: 'Somerset' },
            { stateCode: 'LE', stateName: 'South Ayrshire' },
            { stateCode: 'LF', stateName: 'South Glamorgan' },
            { stateCode: 'LG', stateName: 'South Gloucesteshire' },
            { stateCode: 'LH', stateName: 'South Lanarkshire' },
            { stateCode: 'LI', stateName: 'South Yorkshire' },
            { stateCode: 'LJ', stateName: 'Staffordshire' },
            { stateCode: 'LK', stateName: 'Stirling' },
            { stateCode: 'LL', stateName: 'Stockton On Tees' },
            { stateCode: 'LM', stateName: 'Suffolk' },
            { stateCode: 'LN', stateName: 'Surrey' },
            { stateCode: 'LO', stateName: 'Swansea' },
            { stateCode: 'LP', stateName: 'Torfaen' },
            { stateCode: 'LQ', stateName: 'Tyne and Wear' },
            { stateCode: 'LR', stateName: 'Tyrone' },
            { stateCode: 'LS', stateName: 'Vale Of Glamorgan' },
            { stateCode: 'LT', stateName: 'Wales' },
            { stateCode: 'LU', stateName: 'Warwickshire' },
            { stateCode: 'LV', stateName: 'West Berkshire' },
            { stateCode: 'LW', stateName: 'West Dunbartonshire' },
            { stateCode: 'LX', stateName: 'West Glamorgan' },
            { stateCode: 'LY', stateName: 'West Lothian' },
            { stateCode: 'LZ', stateName: 'West Midlands' },
            { stateCode: 'M0', stateName: 'West Sussex' },
            { stateCode: 'M1', stateName: 'West Yorkshire' },
            { stateCode: 'M2', stateName: 'Western Isles' },
            { stateCode: 'M3', stateName: 'Wiltshire' },
            { stateCode: 'M4', stateName: 'Wirral' },
            { stateCode: 'M5', stateName: 'Worcestershire' },
            { stateCode: 'M6', stateName: 'Wrexham' },
            { stateCode: 'M7', stateName: 'York' },
        ];
        ca.states = [
            { stateCode: 'ON', stateName: 'Ontario' },
            { stateCode: 'QC', stateName: 'Quebec' },
            { stateCode: 'NS', stateName: 'Nova Scotia' },
            { stateCode: 'NB', stateName: 'New Brunswick' },
            { stateCode: 'MB', stateName: 'Manitoba' },
            { stateCode: 'BC', stateName: 'British Columbia' },
            { stateCode: 'PE', stateName: 'Prince Edward Island' },
            { stateCode: 'SK', stateName: 'Saskatchewan' },
            { stateCode: 'AB', stateName: 'Alberta' },
            { stateCode: 'NL', stateName: 'Newfoundland and Labrador' },
        ];
        return [ca, uk, us];
    }
    static currentSiteId() {
        const tags = document.getElementsByName("eto:site");
        const siteId = tags.length === 0 ? 12 : (tags[0] as HTMLMetaElement).content;
        if (isDev)
        console.log("Site ID:"+siteId);
        return siteId;
    }
    static isSiteMovistarMX() {
        const siteId = this.currentSiteId();
        return siteId == "37";
    }
    static isSiteETO() {
        const siteId = this.currentSiteId();
        return siteId == "12";
    }
    static isSitePosIT() {        
        const siteId = this.currentSiteId();
        return siteId == "54";
    }
    static isSiteCodesDirect() {        
        const siteId = this.currentSiteId();
        return siteId == "55";
    }
    static isSiteVodafoneFiji() {
        const siteId = this.currentSiteId();
        return siteId == "43";
    }
    static isSiteRedemptionPIN() {
        // to identify Cashwave rules and ETO Gift Card PIN
        const siteId = this.currentSiteId();
        return siteId === "50" || siteId === "51" || siteId === "52" || siteId === "53" || siteId === "54" || siteId === "55" || siteId === "43";
    }
    static loadCreditCardDetails = async (sessionId: string, sourceCurrencyCode: string) => {
        const response = await CatalogService.fetchCardsAndCounties(sessionId, sourceCurrencyCode);
        if (!response || response.responseCode !== 0) return undefined;
        const { cards: storedCreditCards, countries, addressSettings, creditCardSettings, siteSettings } = response.payLoad;
        const creditCardHelper = CreditCardFactory.getHelper(creditCardSettings);
        return {
            storedCreditCards: storedCreditCards,
            paymentCountries: countries,
            addressSettings: addressSettings,
            siteSettings: siteSettings,
            creditCardHelper
        };
    }

    static checkCardExpired(cardExpiryMMYY: string) {
        var dateObj = new Date();
        var month = dateObj.getUTCMonth() + 1; //months from 1-12
        var year = dateObj.getUTCFullYear();
        var blnExpired = false;
        var yy, mm;

        mm = parseInt((yy = cardExpiryMMYY.substring(0, 2)));
        if (cardExpiryMMYY.length > 5) {
            yy = parseInt(cardExpiryMMYY.substring(cardExpiryMMYY.length - 4));
        } else {
            yy = parseInt(cardExpiryMMYY.substring(cardExpiryMMYY.length - 2));
            year = parseInt(year.toString().substring(2));
        }

        if (yy < year || (yy === year && mm <= month)) {
            blnExpired = true;
        } else {
            blnExpired = false;
        }
        return blnExpired;
    }
}